import { useMemo } from 'react';
import { toLocalDateStr, checkRecurringOverlap } from '../../../../../utils/leaderUtils';

/**
 * Custom hook for managing and formatting resource conflicts in the WorkerSidebar.
 * Handles workers, vehicles, and teams (kolonnen).
 * 
 * @param {Object} params - Hook parameters
 * @param {Array} params.workers - List of all workers
 * @param {Array} params.vehicles - List of all vehicles
 * @param {Array} params.assignments - Full assignment list
 * @param {Array} params.absences - Full absence list
 * @param {Array} params.calculatedConflicts - Pre-calculated global conflicts from LeaderPage
 * @returns {Object} Conflict detection and formatting utilities
 */
export function useWorkerSidebarConflicts({
    workers,
    vehicles,
    assignments,
    absences,
    calculatedConflicts
}) {
    /** Helper to get current (non-historical) conflicts for badges */
    const currentConflicts = useMemo(() => {
        if (!calculatedConflicts) return [];
        const todayStr = toLocalDateStr(new Date());
        return calculatedConflicts.filter(c => c.date >= todayStr);
    }, [calculatedConflicts]);

    const formatConflictDate = (date) => {
        if (!date) return '—';
        const parts = String(date).split('-');
        if (parts.length !== 3) return String(date);
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
    };

    const formatConflictRange = (startDate, endDate) => {
        if (!startDate) return '—';
        const start = formatConflictDate(startDate);
        const end = endDate ? formatConflictDate(endDate) : start;
        return start === end ? start : `${start} - ${end}`;
    };

    const isNextDay = (currentDate, nextDate) => {
        if (!currentDate || !nextDate) return false;
        const a = new Date(`${currentDate}T00:00:00`);
        const b = new Date(`${nextDate}T00:00:00`);
        if (isNaN(a.getTime()) || isNaN(b.getTime())) return false;
        a.setDate(a.getDate() + 1);
        return a.toDateString() === b.toDateString();
    };

    const normalizeConflictName = (value, fallback = 'Eintrag') => {
        const text = String(value || '').trim();
        return text || fallback;
    };

    const normalizeConflictReason = (value, fallback = 'Abwesenheit') => {
        const text = String(value || '').trim();
        if (!text) return fallback;
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    const createRawConflictBlock = (startDate, endDate, reason, items = []) => ({
        startDate,
        endDate,
        reason,
        items: [...new Set((items || []).map(item => String(item || '').trim()).filter(Boolean))].sort()
    });

    const rawBlockSignature = (block) => {
        const items = [...(block.items || [])].sort().join('|');
        return `${block.reason || ''}::${items}`;
    };

    const mergeConflictBlocks = (blocks) => {
        const deduped = [];
        (blocks || []).forEach(block => {
            if (!block?.startDate || !block?.reason) return;
            const signature = `${block.startDate}::${block.endDate || block.startDate}::${rawBlockSignature(block)}`;
            if (!deduped.some(existing => {
                const existingSignature = `${existing.startDate}::${existing.endDate || existing.startDate}::${rawBlockSignature(existing)}`;
                return existingSignature === signature;
            })) {
                deduped.push({
                    startDate: block.startDate,
                    endDate: block.endDate || block.startDate,
                    reason: block.reason,
                    items: [...new Set((block.items || []).map(item => String(item || '').trim()).filter(Boolean))].sort()
                });
            }
        });

        deduped.sort((a, b) => {
            const dateCompare = String(a.startDate).localeCompare(String(b.startDate));
            if (dateCompare !== 0) return dateCompare;
            const reasonCompare = String(a.reason).localeCompare(String(b.reason));
            if (reasonCompare !== 0) return reasonCompare;
            return a.items.join('|').localeCompare(b.items.join('|'));
        });

        const merged = [];
        deduped.forEach(block => {
            const last = merged[merged.length - 1];
            const blockSignature = rawBlockSignature(block);
            if (last && rawBlockSignature(last) === blockSignature && isNextDay(last.endDate, block.startDate)) {
                last.endDate = block.endDate;
                return;
            }
            merged.push({ ...block });
        });

        return merged;
    };

    const toDisplayConflictBlocks = (blocks) => (
        (blocks || []).map(block => ({
            dateRange: block.startDate ? formatConflictRange(block.startDate, block.endDate) : null,
            reason: block.reason,
            items: [...(block.items || [])]
        }))
    );

    const getConflictEntryName = (conflict) => {
        if (!conflict) return 'Eintrag';
        return conflict.assignment?.project_name
            || conflict.assignment?.name
            || conflict.assignment?.title
            || conflict.resourceName
            || 'Eintrag';
    };

    const getAssignmentLabel = (assignment) => {
        if (!assignment) return 'Einsatz';
        return assignment.project_name || assignment.name || assignment.title || 'Einsatz';
    };

    const getConflictAssignmentNames = (conflict) => {
        const names = [];
        (conflict?.assignments || []).forEach(assignment => {
            const label = getAssignmentLabel(assignment);
            if (label && !names.includes(label)) names.push(label);
        });
        if (conflict?.assignment) {
            const label = getAssignmentLabel(conflict.assignment);
            if (label && !names.includes(label)) names.push(label);
        }
        return names;
    };

    const buildWorkerConflictBlocks = (conflicts) => {
        const dayMap = {};
        (conflicts || []).forEach(conflict => {
            const date = conflict?.date;
            if (!date) return;
            if (!dayMap[date]) {
                dayMap[date] = {
                    overlapNames: new Set(),
                    absences: new Map()
                };
            }

            if (conflict.absence) {
                const entryName = normalizeConflictName(getConflictEntryName(conflict));
                const reason = normalizeConflictReason(conflict.absence?.absence_type || conflict.reason);
                dayMap[date].absences.set(`${reason}::${entryName}`, { reason, entryName });
                return;
            }

            getConflictAssignmentNames(conflict).forEach(name => dayMap[date].overlapNames.add(name));
        });

        const rawBlocks = [];
        Object.keys(dayMap).sort().forEach(date => {
            const current = dayMap[date];
            if (current.overlapNames.size > 0) {
                rawBlocks.push({
                    ...createRawConflictBlock(date, date, 'Mehrfachbelegung', [...current.overlapNames].sort()),
                    type: 'collision',
                    priority: 2
                });
            }
            current.absences.forEach(abs => {
                rawBlocks.push(createRawConflictBlock(date, date, abs.reason, [abs.entryName]));
            });
        });

        return toDisplayConflictBlocks(mergeConflictBlocks(rawBlocks));
    };

    /** Get specific conflicts for one worker */
    const getWorkerConflicts = (workerId) => {
        const conflicts = currentConflicts.filter(c => c.type === 'worker' && String(c.resourceId) === String(workerId));
        if (conflicts.length === 0) return null;

        const blocks = buildWorkerConflictBlocks(conflicts);
        if (blocks.length === 0) return null;
        return { type: 'conflict', blocks };
    };

    /** Get specific conflicts for one vehicle */
    const getVehicleConflicts = (vehicleId) => {
        const conflicts = currentConflicts.filter(c => c.type === 'vehicle' && String(c.resourceId) === String(vehicleId));
        
        const dayMap = {};
        conflicts.forEach(conflict => {
            const date = conflict?.date;
            if (!date) return;
            if (!dayMap[date]) {
                dayMap[date] = {
                    overlapNames: new Set(),
                    statusConflicts: new Map()
                };
            }
            
            if (conflict.reason && conflict.reason !== 'Mehrfachbelegung') {
                const entryName = normalizeConflictName(getConflictEntryName(conflict));
                const reason = normalizeConflictReason(conflict.reason);
                dayMap[date].statusConflicts.set(`${reason}::${entryName}`, { reason, entryName });
            } else {
                getConflictAssignmentNames(conflict).forEach(name => dayMap[date].overlapNames.add(name));
            }
        });

        const rawBlocks = [];
        Object.keys(dayMap).sort().forEach(date => {
            const current = dayMap[date];
            if (current.overlapNames.size > 0) {
                rawBlocks.push({
                    ...createRawConflictBlock(date, date, 'Mehrfachbelegung', [...current.overlapNames].sort()),
                    type: 'collision',
                    priority: 2
                });
            }
            current.statusConflicts.forEach(sc => {
                rawBlocks.push(createRawConflictBlock(date, date, sc.reason, [sc.entryName]));
            });
        });

        const blocks = toDisplayConflictBlocks(mergeConflictBlocks(rawBlocks));

        if (blocks.length === 0) return null;
        return { type: 'conflict', blocks };
    };

    /** Get aggregated conflicts for a team (kolonne) */
    const getKolonneConflicts = (kolonne) => {
        const grouped = new Map();
        (kolonne.members || []).forEach(m => {
            let conf = null;
            if (m.type === 'worker') conf = getWorkerConflicts(m.resource_id);
            else if (m.type === 'vehicle') conf = getVehicleConflicts(m.resource_id);

            if (!conf?.blocks?.length) return;
            conf.blocks.forEach(block => {
                if (!block?.dateRange || !block?.reason) return;
                const key = `${block.dateRange}::${block.reason}`;
                if (!grouped.has(key)) {
                    grouped.set(key, {
                        dateRange: block.dateRange,
                        reason: block.reason,
                        items: new Set()
                    });
                }
                const target = grouped.get(key);
                (block.items || []).forEach(value => {
                    if (value) target.items.add(value);
                });
            });
        });

        if (grouped.size === 0) return null;
        const blocks = [...grouped.values()].map(item => ({
            dateRange: item.dateRange,
            reason: item.reason,
            items: [...item.items].sort()
        }));

        return {
            type: 'conflict',
            blocks: blocks.slice(0, 8)
        };
    };

    /** Get status badge for a worker (Free, Assigned, Absent) */
    const getWorkerStatus = (workerId) => {
        const workerAbs = (absences || []).filter(a => String(a.worker_id) === String(workerId));
        const now = new Date();
        const dummyAssignment = {
            start_datetime: now,
            end_datetime: now,
            all_day: false
        };

        let activeAbsence = null;
        try {
            for (const abs of workerAbs) {
                if (checkRecurringOverlap(abs, dummyAssignment)) {
                    activeAbsence = abs;
                    break;
                }
            }
        } catch (e) { console.error("Status check error", e); }

        if (activeAbsence) {
            let type = (activeAbsence.absence_type || '').toLowerCase();
            if (type === 'dienstreise') type = 'termin';
            
            if (type === 'urlaub') return { label: 'Urlaub', dotColor: '#007aff' };
            if (type === 'krank') return { label: 'Krankheit', dotColor: '#ff3b30' };
            if (type === 'termin') return { label: 'Termin', dotColor: '#ff9500' };
            return { label: 'Sonstiges', dotColor: '#af52de' };
        }

        return { label: 'Anwesend', dotClass: 'bg-green-500' };
    };

    /** Get status badge for a vehicle (Ready, Limited, Maintenance) */
    const getVehicleStatus = (vehicleId) => {
        const vehicle = (vehicles || []).find(v => String(v.id) === String(vehicleId));
        if (!vehicle) return null;

        const status = vehicle.status || 'green';
        if (status === 'red') return { label: 'Defekt / Werkstatt', dotColor: '#ef4444' };
        if (status === 'yellow') return { label: 'Eingeschränkt', dotColor: '#f59e0b' };
        return { label: 'Einsatzbereit', dotColor: '#10b981' };
    };

    return {
        getWorkerConflicts,
        getVehicleConflicts,
        getKolonneConflicts,
        getWorkerStatus,
        getVehicleStatus
    };
}
